/**
 * AI 推送接口封装
 * 对应后端：mplat-admin AiPushController（/aiPush/*，全部免 token）
 *
 * 路径策略（对齐 mplat-client）：
 * - 前端统一用 /api/xxx 相对路径
 * - 开发环境：vue.config.js devServer.proxy 把 /api 转发到后端，pathRewrite ^/api → /mplat-admin
 * - 生产环境：nginx 把 /api 反向代理到后端 /mplat-admin
 * - 前端代码在开发和生产环境都不需要改动
 *
 * 缓存策略：
 * - 不做前端缓存，缓存统一在后端 Redis 侧处理（预热 + 接口缓存）
 * - 前端每次都发请求，由后端保证响应速度
 */
const BASE = '/api/aiPush'

// ── 请求封装 ──────────────────────────────
async function post(path, body = {}) {
  const resp = await fetch(BASE + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {})
  })
  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status} ${resp.statusText}`)
  }
  const json = await resp.json()
  if (json.code !== 0) {
    throw new Error(json.msg || '请求失败')
  }
  return { data: json.data, total: json.total }
}

// ── 接口 ──────────────────────────────────

/** 1. 推送/更新每日内容（幂等 upsert） */
export function upsert(payload) {
  return post('/upsert', payload)
}

/** 2. 批量推送（初始化导入） */
export function batchUpsert(items) {
  return post('/batchUpsert', { items })
}

/** 3. 按日期查单篇（前端知识页加载当天文章） */
export function getByDate(pushDate) {
  return post('/getByDate', { push_date: pushDate })
}

/** 4. 分页查询列表（日期区间 + 主题 + 关键词） */
export function list({ startDate, endDate, topicCode, keyword, page = 1, size = 10 } = {}) {
  return post('/list', {
    startDate, endDate, topic_code: topicCode, keyword, page, size
  })
}

/** 5. 日期导航列表（前端日期 pill/下拉框） */
export function dateList(limit = 60) {
  return post('/dateList', { limit })
}

/** 6. 主题字典查询 */
export function topics() {
  return post('/topics', {})
}

/** 7. 软删某天内容 */
export function remove(pushDate) {
  return post('/delete', { push_date: pushDate })
}

/** 8. 阅读量 +1（Redis 缓冲计数），返回实时总阅读量 */
export function incrView(pushDate) {
  return post('/incrView', { push_date: pushDate })
}

/** 9. 相关笔记推荐（同主题，按日期倒序） */
export function related(pushDate, limit = 5) {
  return post('/related', { push_date: pushDate, limit })
}

/** 10. 学习统计看板（按年聚合） */
export function stats(year) {
  return post('/stats', year ? { year } : {})
}

/** 11. 标签云（全量标签 + 计数） */
export function tags() {
  return post('/tags', {})
}

/** 12. 按标签查列表 */
export function listByTag(tag, page = 1, size = 10) {
  return post('/listByTag', { tag, page, size })
}

export default {
  upsert, batchUpsert, getByDate, list, dateList, topics, remove, incrView, related, stats, tags, listByTag
}
